'use client';

import { useEffect, useRef } from 'react';
import { listPendingObservingVideoUploadsForCounsel } from './pendingObservingVideoUploadStore';
import {
  resumePendingObservingVideoUpload,
  type ObservingVideoUploadFlowDeps,
} from './observingVideoUploadPipeline';

export interface UseResumePendingObservingVideoUploadsOptions {
  visitCounselId: string;
  enabled: boolean;
  showToast: ObservingVideoUploadFlowDeps['showToast'];
  refetch: ObservingVideoUploadFlowDeps['refetch'];
  markPending: ObservingVideoUploadFlowDeps['markPending'];
  clearPending: ObservingVideoUploadFlowDeps['clearPending'];
}

/**
 * 상담 시작 페이지 mount 시, 새로고침으로 끊긴 presigned 영상 업로드를 재개한다.
 */
export function useResumePendingObservingVideoUploads({
  visitCounselId,
  enabled,
  showToast,
  refetch,
  markPending,
  clearPending,
}: UseResumePendingObservingVideoUploadsOptions): void {
  const depsRef = useRef({
    showToast,
    refetch,
    markPending,
    clearPending,
  });
  depsRef.current = { showToast, refetch, markPending, clearPending };

  useEffect(() => {
    if (!enabled || !visitCounselId) return;

    let cancelled = false;

    void (async () => {
      const jobs = await listPendingObservingVideoUploadsForCounsel(visitCounselId);
      if (cancelled || jobs.length === 0) return;

      const deps: ObservingVideoUploadFlowDeps = {
        ...depsRef.current,
        resumedToastMessage: '영상 업로드가 이어서 완료되었습니다.',
      };

      for (const job of jobs) {
        if (cancelled) break;
        await resumePendingObservingVideoUpload(job, deps);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled, visitCounselId]);
}
